import React, { useState, useEffect } from 'react';
import { LuCalendar, LuMapPin, LuClock, LuCheck } from 'react-icons/lu';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../shared/firebase';
import { extractString, extractOrder } from '../../../shared/fieldUtils';
import prayerRosary from '../../../assets/prayer_rosary.jpg';

export default function UpdatesRow({ onSelectSection }) {
  const [newsList, setNewsList] = useState([]);
  const [eventsList, setEventsList] = useState([]);
  const [prayerSubmitted] = useState(false);
  const [prayerList, setPrayerList] = useState([
    {
      id: 'default-1',
      text: 'Let us pray for peace in the world and for the sick and suffering in our community.',
      author: 'Anonymous'
    }
  ]);
  const [currentPrayerIdx, setCurrentPrayerIdx] = useState(0);

  useEffect(() => {
    let isMounted = true;

    async function fetchHomeUpdates() {
      try {
        // 1. Fetch News from Firestore 'news'
        let newsSnap = await getDocs(collection(db, 'news')).catch(() => null);
        if (newsSnap && !newsSnap.empty) {
          const fetchedNews = newsSnap.docs.map(doc => {
            const d = doc.data();
            return {
              id: doc.id,
              title: extractString(d.title || d.heading || d.news_title, "Diocesan News"),
              date: extractString(d.date || d.created_at || d.publish_date, "Recent"),
              img: extractString(d.img || d.image || d.img_url || d.image_url, "https://images.unsplash.com/photo-1548625149-fc4a29cf7092?auto=format&fit=crop&w=400&q=80"),
              order: extractOrder(d.order)
            };
          });
          fetchedNews.sort((a, b) => a.order - b.order);
          if (isMounted && fetchedNews.length > 0) {
            setNewsList(fetchedNews.slice(0, 3));
          }
        }

        // 2. Fetch Events from Firestore 'events' / 'event'
        let eventSnap = await getDocs(collection(db, 'events')).catch(() => null);
        if (!eventSnap || eventSnap.empty) {
          eventSnap = await getDocs(collection(db, 'event')).catch(() => null);
        }
        if (!eventSnap || eventSnap.empty) {
          eventSnap = await getDocs(collection(db, 'calendar')).catch(() => null);
        }

        if (eventSnap && !eventSnap.empty) {
          const fetchedEvents = eventSnap.docs.map(doc => {
            const d = doc.data();
            let rawDate = extractString(d.date || d.event_date || d.created_at);
            let day = extractString(d.day);
            let month = extractString(d.month);
            let year = extractString(d.year);

            if (rawDate) {
              const parsedDate = new Date(rawDate);
              if (!isNaN(parsedDate.getTime())) {
                if (!day) day = parsedDate.getDate().toString();
                if (!month) month = parsedDate.toLocaleString('en-US', { month: 'short' }).toUpperCase();
                if (!year) year = parsedDate.getFullYear().toString();
              } else {
                const parts = rawDate.split(/[-/ ,]/).filter(Boolean);
                if (parts.length >= 3) {
                  if (parts[0].length === 4) {
                    year = parts[0];
                    month = isNaN(parts[1]) ? parts[1].substring(0, 3).toUpperCase() : parts[1];
                    day = parts[2];
                  } else {
                    day = parts[0];
                    month = isNaN(parts[1]) ? parts[1].substring(0, 3).toUpperCase() : parts[1];
                    year = parts[2];
                  }
                } else if (parts.length === 2) {
                  day = parts[0];
                  month = parts[1].substring(0, 3).toUpperCase();
                }
              }
            }

            if (!day) day = "15";
            if (!month) month = "OCT";
            if (!year) year = new Date().getFullYear().toString();

            return {
              id: doc.id,
              day,
              month: month.substring(0, 3).toUpperCase(),
              year,
              title: extractString(d.title || d.event_name || d.name, "Diocesan Gathering"),
              location: extractString(d.location || d.place || d.venue, "Diocesan Centre"),
              time: extractString(d.time || d.timing, "10:00 AM"),
              order: extractOrder(d.order)
            };
          });
          fetchedEvents.sort((a, b) => a.order - b.order);
          if (isMounted && fetchedEvents.length > 0) {
            setEventsList(fetchedEvents.slice(0, 3));
          }
        }

        // 3. Fetch Prayer Items from Firestore ('contact-us', 'prayer_wall', 'prayers', 'prayer_requests', 'prayer')
        let allPrayers = [];

        let contactSnap = await getDocs(collection(db, 'contact-us')).catch(() => null);
        if (!contactSnap || contactSnap.empty) {
          contactSnap = await getDocs(collection(db, 'contact_us')).catch(() => null);
        }
        if (contactSnap && !contactSnap.empty) {
          contactSnap.docs.forEach(doc => {
            const d = doc.data();
            // STRICT CHECK: Only show on Home if showOnHome === true or isHomePrayer === true
            const isHome = d.showOnHome === true || d.isHomePrayer === true;
            if (isHome) {
              const text = extractString(d.message || d.Message || d.intention || d.request || d.quote || d.prayer);
              if (text) {
                allPrayers.push({
                  id: doc.id,
                  text,
                  author: extractString(d.name || d.Name || d.author || d.by, "Anonymous"),
                  order: extractOrder(d.order, 999)
                });
              }
            }
          });
        }

        // Also check standalone prayer collections if available
        let prayerSnap = await getDocs(collection(db, 'prayer_wall')).catch(() => null);
        if (!prayerSnap || prayerSnap.empty) {
          prayerSnap = await getDocs(collection(db, 'prayers')).catch(() => null);
        }
        if (!prayerSnap || prayerSnap.empty) {
          prayerSnap = await getDocs(collection(db, 'prayer_requests')).catch(() => null);
        }
        if (!prayerSnap || prayerSnap.empty) {
          prayerSnap = await getDocs(collection(db, 'prayer')).catch(() => null);
        }

        if (prayerSnap && !prayerSnap.empty) {
          prayerSnap.docs.forEach(doc => {
            const d = doc.data();
            const isExplicitlyFalse = d.showOnHome === false || d.isHomePrayer === false;
            const isHome = d.showOnHome === true || d.isHomePrayer === true || (d.showOnHome === undefined && d.isHomePrayer === undefined);
            if (isHome && !isExplicitlyFalse) {
              const text = extractString(d.quote || d.prayer || d.request || d.message || d.text || d.content || d.intention);
              if (text) {
                allPrayers.push({
                  id: doc.id,
                  text,
                  author: extractString(d.author || d.by || d.name || d.Name || d.submitted_by, "Anonymous"),
                  order: extractOrder(d.order, 999)
                });
              }
            }
          });
        }

        if (allPrayers.length > 0) {
          allPrayers.sort((a, b) => a.order - b.order);
          if (isMounted) {
            setPrayerList(allPrayers);
          }
        }
      } catch (err) {
        console.error("Error fetching home updates:", err);
      }
    }

    fetchHomeUpdates();
    return () => { isMounted = false; };
  }, []);

  // Auto-rotate prayers if multiple items exist
  useEffect(() => {
    if (prayerList.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentPrayerIdx(prev => (prev + 1) % prayerList.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [prayerList.length]);

  return (
    <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 my-1 sm:my-2 relative z-20">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── 1. Latest News Card ── */}
        <div className="bg-white rounded-[28px] p-6 sm:p-7 shadow-[0_4px_30px_rgba(0,0,0,0.03)] border border-[#ECE5E0] flex flex-col justify-between hover:shadow-[0_8px_35px_rgba(0,0,0,0.06)] transition-all">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-serif font-bold text-[22px] sm:text-[24px] text-[#842343]">
              Latest <span className="text-[#1a1715]">News</span>
            </h2>
            <button
              onClick={(e) => { e.preventDefault(); onSelectSection('news'); }}
              className="text-[#842343] hover:text-[#63142c] text-[13px] font-semibold flex items-center gap-1.5 transition-colors group cursor-pointer"
            >
              <span>View All</span>
              <span className="text-[14px] group-hover:translate-x-1 transition-transform">→</span>
            </button>
          </div>

          <div className="flex flex-col gap-4 justify-between flex-1">
            {newsList.map((item, idx) => (
              <div
                key={item.id || idx}
                onClick={() => onSelectSection('news')}
                className="flex items-center gap-4 group cursor-pointer p-1.5 -mx-1.5 rounded-xl hover:bg-[#FAF6F2]/80 transition-colors"
              >
                <div className="w-[105px] sm:w-[110px] h-[72px] rounded-xl overflow-hidden shrink-0 border border-gray-100 shadow-2xs bg-[#FAF6F2]">
                  <img
                    src={item.img}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex flex-col justify-center min-w-0 flex-1">
                  <h3 className="font-serif font-bold text-[#1e1a18] text-[14px] sm:text-[14.5px] leading-snug mb-1.5 group-hover:text-[#842343] transition-colors line-clamp-2">
                    {item.title}
                  </h3>
                  <div className="flex items-center gap-1.5 text-[#8A7E79] text-[11.5px] font-medium">
                    <LuCalendar className="w-3.5 h-3.5 text-[#B83253]" />
                    <span>{item.date}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── 2. Upcoming Events Card ── */}
        <div className="bg-white rounded-[28px] p-6 sm:p-7 shadow-[0_4px_30px_rgba(0,0,0,0.03)] border border-[#ECE5E0] flex flex-col justify-between hover:shadow-[0_8px_35px_rgba(0,0,0,0.06)] transition-all">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-serif font-bold text-[22px] sm:text-[24px] text-[#842343]">
              Upcoming Events
            </h2>
            <button
              onClick={(e) => { e.preventDefault(); onSelectSection('news'); }}
              className="text-[#842343] hover:text-[#63142c] text-[13px] font-semibold flex items-center gap-1.5 transition-colors group cursor-pointer"
            >
              <span>View All</span>
              <span className="text-[14px] group-hover:translate-x-1 transition-transform">→</span>
            </button>
          </div>

          <div className="flex flex-col justify-between flex-1 relative">
            {/* Vertical connector line connecting event dates */}
            <div className="absolute left-[30px] top-5 bottom-5 w-[2px] bg-gradient-to-b from-[#842343]/30 via-[#842343]/20 to-[#842343]/30 rounded-full"></div>

            <div className="flex flex-col justify-between h-full gap-4 sm:gap-5 relative z-10">
              {eventsList.map((event, idx) => (
                <div
                  key={event.id || idx}
                  onClick={() => onSelectSection('news')}
                  className="flex items-center gap-3.5 relative group cursor-pointer p-2 -mx-2 rounded-2xl hover:bg-[#FAF6F2]/80 transition-all duration-300"
                >
                  <div className="flex flex-col items-center justify-center w-[64px] py-1.5 px-1 bg-[#FAF6F2] rounded-xl border border-[#E8DDD7]/70 text-center shrink-0 shadow-2xs group-hover:bg-[#842343] group-hover:border-[#842343] transition-all duration-300">
                    <span className="font-serif font-bold text-[20px] leading-none text-[#1a1715] group-hover:text-white transition-colors">
                      {event.day}
                    </span>
                    <span className="font-sans font-extrabold text-[9.5px] tracking-wider text-[#842343] group-hover:text-[#E8B86D] uppercase mt-0.5 transition-colors">
                      {event.month}
                    </span>
                    <span className="font-sans font-bold text-[9px] text-[#8A7E79] group-hover:text-white/80 transition-colors">
                      {event.year}
                    </span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif font-bold text-[#1e1a18] text-[14px] sm:text-[14.5px] leading-snug mb-1 group-hover:text-[#842343] transition-colors truncate">
                      {event.title}
                    </h3>
                    <div className="flex items-center gap-3 text-[#8A7E79] text-[11.5px] font-medium">
                      <span className="flex items-center gap-1 truncate">
                        <LuMapPin className="w-3 h-3 text-[#B83253] shrink-0" />
                        {event.location}
                      </span>
                      <span className="flex items-center gap-1 shrink-0">
                        <LuClock className="w-3 h-3 text-[#B83253] shrink-0" />
                        {event.time}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── 3. Prayer Wall Card ── */}
        <div className="bg-white rounded-[28px] p-6 sm:p-7 shadow-[0_4px_30px_rgba(0,0,0,0.03)] border border-[#ECE5E0] flex flex-col justify-between hover:shadow-[0_8px_35px_rgba(0,0,0,0.06)] transition-all">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-serif font-bold text-[22px] sm:text-[24px] text-[#842343]">
              Prayer With Us
            </h2>
            <button
              onClick={(e) => { e.preventDefault(); onSelectSection('contact'); }}
              className="text-[#842343] hover:text-[#63142c] text-[13px] font-semibold flex items-center gap-1.5 transition-colors group cursor-pointer"
            >
              <span>View All</span>
              <span className="text-[14px] group-hover:translate-x-1 transition-transform">→</span>
            </button>
          </div>

          {/* Soft Cream Inner Card matching Reference Image */}
          <div className="bg-gradient-to-br from-[#FAF5EF] via-[#FAF7F2] to-[#F5ECE5] rounded-2xl p-5 sm:p-6 border border-[#E8DDD7] flex-1 flex flex-col justify-between relative overflow-hidden group">
            {/* Rosary image in right background */}
            <div className="absolute right-0 bottom-0 top-0 w-1/2 overflow-hidden pointer-events-none opacity-95 group-hover:scale-105 transition-transform duration-700">
              <img
                src={prayerRosary}
                alt="Rosary Cross"
                className="w-full h-full object-cover object-right"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#FAF7F2] via-[#FAF7F2]/60 to-transparent"></div>
            </div>

            <div className="relative z-10 max-w-[65%] text-left space-y-2">
              <span className="font-serif font-bold text-[#c39b5b] text-4xl sm:text-5xl leading-none block -mb-2 opacity-90 select-none">
                “
              </span>
              <p className="font-serif font-bold text-[#1a1715] text-[15px] sm:text-[16px] leading-[1.4] drop-shadow-2xs transition-all duration-300">
                {prayerList[currentPrayerIdx]?.text?.trim() || 'Let us pray for peace in the world and for the sick and suffering in our community.'}
              </p>
              <p className="text-[#8A7E79] text-xs font-sans font-semibold">
                — {prayerList[currentPrayerIdx]?.author?.trim() || 'Anonymous'}
              </p>
            </div>

            {prayerList.length > 1 && (
              <div className="relative z-10 flex items-center gap-1.5 mt-3">
                {prayerList.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPrayerIdx(i)}
                    className={`h-1.5 rounded-full transition-all cursor-pointer ${
                      i === currentPrayerIdx ? 'w-5 bg-[#842343]' : 'w-1.5 bg-[#842343]/30 hover:bg-[#842343]/60'
                    }`}
                    aria-label={`Go to prayer request ${i + 1}`}
                  />
                ))}
              </div>
            )}

            <div className="relative z-10 mt-5 text-left">
              {prayerSubmitted ? (
                <div className="bg-[#842343] p-2.5 rounded-full text-center text-white text-xs font-bold flex items-center justify-center gap-1.5 shadow-sm">
                  <LuCheck className="w-4 h-4 text-[#E8B86D]" />
                  <span>Prayer Received</span>
                </div>
              ) : (
                <button
                  onClick={() => onSelectSection('contact')}
                  className="bg-[#842343] hover:bg-[#6c1b35] text-white text-xs font-bold px-5 py-2.5 rounded-full shadow-sm hover:shadow-md transition-all cursor-pointer"
                >
                  Submit Prayer Request
                </button>
              )}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
